import { NOTIFICATION_SCHEDULE } from './constants.js';

const ALLOWED_FREQUENCY_HOURS = [24, 48, 168];
const DEFAULT_FREQUENCY_HOURS = NOTIFICATION_SCHEDULE.DEFAULT_FREQUENCY_HOURS;
const CRON_RE = /^(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)$/;

function parseHhMm(startTime) {
  if (typeof startTime !== 'string' || !/^\d{2}:\d{2}$/.test(startTime)) {
    throw new Error('startTime must be HH:mm');
  }
  const [h, m] = startTime.split(':').map(Number);
  if (h > 23 || m > 59) {
    throw new Error('startTime must be HH:mm');
  }
  return { hour: h, minute: m };
}

export function normalizeFrequencyHours(value) {
  if (value == null || value === '') return DEFAULT_FREQUENCY_HOURS;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_FREQUENCY_HOURS;
  if (!ALLOWED_FREQUENCY_HOURS.includes(n)) {
    throw new Error(`frequencyHours must be one of ${ALLOWED_FREQUENCY_HOURS.join(', ')}`);
  }
  return n;
}

export function shiftUtcFromLocal({ startTime, utcOffsetMinutes = 0, anchorDate }) {
  const { hour, minute } = parseHhMm(startTime);
  const offset = Number(utcOffsetMinutes) || 0;
  let utcMinutes = hour * 60 + minute - offset;
  let dayShift = 0;
  while (utcMinutes < 0) {
    utcMinutes += 1440;
    dayShift -= 1;
  }
  while (utcMinutes >= 1440) {
    utcMinutes -= 1440;
    dayShift += 1;
  }
  const utcHour = Math.floor(utcMinutes / 60);
  const utcMin = utcMinutes % 60;
  let anchorUtcDate = null;
  let weekday = null;
  if (anchorDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(anchorDate)) {
      throw new Error('anchorDate must be YYYY-MM-DD');
    }
    const [y, mo, d] = anchorDate.split('-').map(Number);
    const utc = new Date(Date.UTC(y, mo - 1, d + dayShift));
    anchorUtcDate = utc.toISOString().slice(0, 10);
    weekday = utc.getUTCDay();
  }
  return { utcHour, utcMin, dayShift, anchorUtcDate, weekday };
}

export function buildScheduleCron(input = {}) {
  const frequencyHours = normalizeFrequencyHours(
    input.frequencyHours == null ? DEFAULT_FREQUENCY_HOURS : input.frequencyHours
  );
  const { utcHour, utcMin, weekday, anchorUtcDate } = shiftUtcFromLocal(input);
  let cron;
  if (frequencyHours === 168) {
    const d = weekday == null ? '*' : String(weekday);
    cron = `${utcMin} ${utcHour} * * ${d}`;
  } else {
    cron = `${utcMin} ${utcHour} * * *`;
  }
  return { cron, frequencyHours, anchorUtcDate };
}

export function parseScheduleCron(cron) {
  if (typeof cron !== 'string') {
    throw new Error('cron must be a string');
  }
  const match = cron.trim().match(CRON_RE);
  if (!match) {
    throw new Error('cron must have 5 fields');
  }
  const minute = Number(match[1]);
  const hour = Number(match[2]);
  const dow = match[5];
  if (!Number.isInteger(minute) || !Number.isInteger(hour) || minute > 59 || hour > 23) {
    throw new Error('cron minute/hour must be integers');
  }
  const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const weekly = dow !== '*';
  return {
    startTime,
    utcOffsetMinutes: 0,
    frequencyHours: weekly ? 168 : 24,
    dow: weekly ? Number(dow) : null,
  };
}

export function scheduleFromLegacyCron(cron) {
  try {
    return parseScheduleCron(cron);
  } catch {
    return null;
  }
}

export function currentUtcOffsetMinutes() {
  return -new Date().getTimezoneOffset();
}

function padHm(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Legacy cron stores a UTC hour token (`24` means midnight). */
export function utcHourTokenFromLocalTime(startTime) {
  try {
    const { hour, minute } = parseHhMm(startTime);
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    const utcHour = date.getUTCHours();
    return utcHour === 0 ? '24' : String(utcHour);
  } catch {
    return '';
  }
}

/** Show a stored UTC hour token as the browser's local HH:mm. */
export function localTimeFromLegacyHour(hourToken) {
  if (hourToken == null || hourToken === '') return '';
  const raw = String(hourToken);
  if (/^\d{2}:\d{2}$/.test(raw)) return raw;
  const utcHour = raw === '24' ? 0 : Number(raw);
  if (!Number.isFinite(utcHour)) return '';
  const date = new Date();
  date.setUTCHours(utcHour, 0, 0, 0);
  return padHm(date.getHours(), date.getMinutes());
}

export function resolveNotificationStartTime(startTime) {
  if (typeof startTime === 'string' && /^\d{2}:\d{2}$/.test(startTime)) {
    return startTime;
  }
  return NOTIFICATION_SCHEDULE.DEFAULT_START_TIME;
}

export function buildSchedulePayload({ startTime } = {}) {
  return {
    startTime: resolveNotificationStartTime(startTime),
    utcOffsetMinutes: currentUtcOffsetMinutes(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    frequencyHours: DEFAULT_FREQUENCY_HOURS,
  };
}

/**
 * Stored startTime is the creator's wall clock. Show the equivalent clock
 * in the current browser timezone for the picker and table.
 */
export function localStartTimeFromSchedule(schedule = {}) {
  const startTime = schedule && schedule.startTime;
  if (!startTime) return NOTIFICATION_SCHEDULE.DEFAULT_START_TIME;
  const storedOffset = schedule.utcOffsetMinutes == null
    ? currentUtcOffsetMinutes()
    : Number(schedule.utcOffsetMinutes) || 0;
  const currentOffset = currentUtcOffsetMinutes();
  if (storedOffset === currentOffset) return startTime;
  try {
    const { hour, minute } = parseHhMm(startTime);
    let localMinutes = hour * 60 + minute - storedOffset + currentOffset;
    while (localMinutes < 0) localMinutes += 1440;
    while (localMinutes >= 1440) localMinutes -= 1440;
    return padHm(Math.floor(localMinutes / 60), localMinutes % 60);
  } catch {
    return startTime;
  }
}

/**
 * Picker time for a notification row. Prefer meta.schedule; otherwise convert
 * schedulerTiming's UTC cron clock into the browser timezone (same fallback as
 * NotificationScheduleCell).
 */
export function localStartTimeFromNotification(notification = {}) {
  const schedule = notification.meta && notification.meta.schedule;
  if (schedule && schedule.startTime) return localStartTimeFromSchedule(schedule);
  const parsed = scheduleFromLegacyCron(notification.schedulerTiming);
  if (parsed) return localStartTimeFromSchedule(parsed);
  return localStartTimeFromSchedule();
}

function utcDaysBetween(aYmd, bDate) {
  const [y, m, d] = aYmd.split('-').map(Number);
  const aMs = Date.UTC(y, m - 1, d);
  const b = bDate instanceof Date ? bDate : new Date(bDate);
  const bMs = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round((bMs - aMs) / 86400000);
}

function resolveFrequencyHours(schedule) {
  if (!schedule || schedule.frequencyHours == null) return DEFAULT_FREQUENCY_HOURS;
  try {
    return normalizeFrequencyHours(schedule.frequencyHours);
  } catch {
    return DEFAULT_FREQUENCY_HOURS;
  }
}

function resolveUtcClock(schedule) {
  if (schedule && schedule.cron) {
    const parsed = parseScheduleCron(schedule.cron);
    const [hour, minute] = parsed.startTime.split(':').map(Number);
    return { utcHour: hour, utcMin: minute, dow: parsed.dow };
  }
  if (schedule && schedule.startTime != null) {
    const shifted = shiftUtcFromLocal(schedule);
    return { utcHour: shifted.utcHour, utcMin: shifted.utcMin, dow: shifted.weekday };
  }
  if (schedule && schedule.schedulerTiming) {
    const parsed = parseScheduleCron(schedule.schedulerTiming);
    const [hour, minute] = parsed.startTime.split(':').map(Number);
    return { utcHour: hour, utcMin: minute, dow: parsed.dow };
  }
  const built = buildScheduleCron({ startTime: '00:00', utcOffsetMinutes: 0, frequencyHours: 24 });
  const parsed = parseScheduleCron(built.cron);
  const [hour, minute] = parsed.startTime.split(':').map(Number);
  return { utcHour: hour, utcMin: minute, dow: null };
}

export function shouldRunOnDate(schedule, now = new Date()) {
  if (!schedule || schedule.frequencyHours == null) return true;
  const frequencyHours = resolveFrequencyHours(schedule);
  const instant = now instanceof Date ? now : new Date(now);
  if (frequencyHours === 168) {
    const { dow } = resolveUtcClock(schedule);
    if (dow == null || Number.isNaN(dow)) return true;
    return instant.getUTCDay() === dow;
  }
  const days = frequencyHours / 24;
  if (days <= 1) return true;
  const anchor = schedule.anchorUtcDate;
  if (!anchor) return true;
  const delta = utcDaysBetween(anchor, instant);
  return delta >= 0 && delta % days === 0;
}

function atUtcOnDay(dayMs, utcHour, utcMin) {
  const d = new Date(dayMs);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), utcHour, utcMin, 0, 0));
}

export function getNextRuns(schedule, count = 5, now = new Date()) {
  const n = Math.max(0, Number(count) || 0);
  const instant = now instanceof Date ? now : new Date(now);
  const { utcHour, utcMin } = resolveUtcClock(schedule);
  const out = [];
  let dayMs = Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
  const dayStep = 24 * 60 * 60 * 1000;
  let guard = 0;
  while (out.length < n && guard < 4000) {
    const candidate = atUtcOnDay(dayMs, utcHour, utcMin);
    if (candidate.getTime() > instant.getTime() && shouldRunOnDate(schedule, candidate)) {
      out.push(candidate);
    }
    dayMs += dayStep;
    guard += 1;
  }
  return out;
}

/** e.g. Fri 29 Oct, 09:00 AM (no comma after weekday) */
export function formatLocalRunLine(date) {
  const instant = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(instant.getTime())) return '';
  const weekday = instant.toLocaleDateString(undefined, { weekday: 'short' }).replace(/,\s*$/, '');
  const month = instant.toLocaleDateString(undefined, { month: 'short' });
  const day = instant.getDate();
  const time = instant.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${weekday} ${day} ${month}, ${time}`;
}

export function getNextRunsLocal(schedule, count = NOTIFICATION_SCHEDULE.NEXT_RUNS_COUNT, now = new Date()) {
  return getNextRuns(schedule, count, now).map((date) => ({
    local: formatLocalRunLine(date),
    utcIso: date.toISOString(),
  }));
}

export function formatNextRunAt(isoUtc) {
  if (!isoUtc) return '';
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startThat = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startThat - startToday) / 86400000);
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Tomorrow, ${time}`;
  return formatLocalRunLine(date);
}

/** Short zone for tooltips (e.g. IST), without GMT offset suffix. */
export function browserTimezoneShortLabel() {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find((part) => part.type === 'timeZoneName');
    const value = tzPart?.value || '';
    if (value && !/^GMT/i.test(value)) return value;
  } catch {
    // fall through
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
}

export function describeSchedule(schedule = {}) {
  const start = localStartTimeFromSchedule(schedule);
  const hours = resolveFrequencyHours(schedule);
  if (hours === 48) return `Every 2 days · ${start}`;
  if (hours === 168) return `Weekly · ${start}`;
  return `Daily · ${start}`;
}

export function browserTimezoneLabel() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  return `Times in ${tz}`;
}
